import dayjs from "dayjs"
import { MULTI_DAY_PRICE_ALLOWED } from "pagesV2/pro/Experiences/ListingScheduling/utils/constants"
import { daysFromApi, timesTo12h, generateStartTimes } from "./scheduleApiFormat"
import { formatSlotLabelInZone } from "../components/scheduling/ScheduleExperienceModal/utils"

/**
 * Expands trip.available_date_ranges into the flat form fields
 * SchedulingStep expects: timeBlocks (index array) + seasonStart_N / seasonEnd_N.
 */
const buildSeasonalRanges = (trip) => {
  const ranges = trip.available_date_ranges
  if (!trip.seasonal || !ranges?.length) {
    return { timeBlocks: trip.seasonal ? [0] : [] }
  }
  const result = { timeBlocks: ranges.map((_, i) => i) }
  ranges.forEach((range, i) => {
    if (range.start) result[`seasonStart_${i}`] = dayjs(range.start)
    if (range.end) result[`seasonEnd_${i}`] = dayjs(range.end)
  })
  return result
}

/**
 * Converts trip server data → AntD form field values.
 * Called once by WizardProvider after the trip is loaded.
 *
 * Rules:
 * - No backend key changes. Field names here match what Form.Item name="" expects.
 * - available_hours strings are converted to dayjs (DaysAndHours renders TimePicker).
 * - Seasonal date ranges (seasonStart_N / seasonEnd_N) are built by buildSeasonalRanges
 *   and spread in. SchedulingStep will skip re-initialization if these are already set.
 */
export const buildDefaultValues = (trip) => {
  if (!trip?.id) return {}

  const availableHours = (trip.available_hours || []).map(({ start, end }) => ({
    start: start ? dayjs(start, "HH:mm") : null,
    end: end ? dayjs(end, "HH:mm") : null
  }))

  return {
    // ── Details ─────────────────────────────────────────────────────────────
    title: trip.title,
    description: trip.description,
    categories: trip.category_id ?? undefined,
    sub_categories: trip.subcategory_ids?.length ? trip.subcategory_ids : undefined,
    min_guests: trip.min_guests,
    max_guests: trip.max_guests,
    languages: trip.languages || [],
    skill_levels: trip.skill_levels || [],
    included_items: trip.included_items,
    bring_items: trip.bring_items,
    accessibility_enabled: trip.accessibility_enabled,
    alcohol_allowed: trip.alcohol_allowed,
    children_allowed: trip.children_allowed,
    // Publishing toggles default to ON (Form.Item initialValue={true}). Seed the matching
    // keys from the server — seeding publish_on_guides_site (a different, unused key) left
    // front-end visibility falling back to true even when the backend returned false.
    // `?? true` keeps an explicit false as false but defaults missing values (legacy trips
    // without the field) to ON, matching the create default.
    publish_on_frontend_embed: trip.publish_on_frontend_embed ?? true,
    publish_on_platform: trip.publish_on_platform ?? true,

    // ── Scheduling ───────────────────────────────────────────────────────────
    time_zone: trip.time_zone,
    duration_type: trip.duration_type || "single_day",
    number_of_days: trip.duration_type === "multi_day" ? (trip.duration_value || undefined) : undefined,
    duration_mode: trip.duration_type === "multi_day" ? (trip.duration_mode || "fixed") : undefined,
    min_duration_days: trip.duration_type === "multi_day" ? (trip.min_duration_days || undefined) : undefined,
    max_duration_days: trip.duration_type === "multi_day" ? (trip.max_duration_days || undefined) : undefined,
    progressive_pricing_enabled: trip.duration_type === "multi_day" ? (trip.progressive_pricing_enabled ?? false) : undefined,
    progressive_pricing_threshold: trip.duration_type === "multi_day" ? (trip.progressive_pricing_threshold || undefined) : undefined,
    progressive_pricing_direction: trip.duration_type === "multi_day" ? (trip.progressive_pricing_direction || undefined) : undefined,
    progressive_pricing_value: trip.duration_type === "multi_day" ? (trip.progressive_pricing_value ?? undefined) : undefined,
    progressive_pricing_unit: trip.duration_type === "multi_day" ? (trip.progressive_pricing_unit || undefined) : undefined,
    progressive_pricing_cap: trip.duration_type === "multi_day" ? (trip.progressive_pricing_cap || undefined) : undefined,
    pricing_type: trip.pricing_type || "per_person",
    payment_type: trip.payment_type || "full",
    deposit_percentage: trip.deposit_percentage || 10,
    full_payment_required: trip.full_payment_required,
    licenses_required: trip.licenses_required,
    licenses_list: trip.licenses_list,
    seasonal: trip.seasonal,
    isSeasonalExperience: trip.seasonal === true,
    recurringYearly: trip.available_date_ranges?.length > 0 ? trip.recurring_yearly : true,
    available_days: trip.available_days,
    available_hours: availableHours.length ? availableHours : undefined,
    ...buildSeasonalRanges(trip),

    // ── Tickets ──────────────────────────────────────────────────────────────
    ticket_types: trip.ticket_types?.length
      ? trip.ticket_types.map(({ max_capacity, ...rest }) => ({ ...rest, maxCapacity: max_capacity }))
      : undefined,
    // Seed base prices from server so the Pricing step shows saved values on edit.
    ticketTypePrices: Object.fromEntries(
      (trip.ticket_types || []).map((t) => [t.id, { enabled: t.enabled !== false, price: t.price > 0 ? t.price : null }])
    ),
    min_tickets: trip.min_tickets,
    max_tickets: trip.max_tickets,
    ticket_description: trip.ticket_description,

    // ── Ticketed Scheduling ───────────────────────────────────────────────────
    duration_value: trip.duration_type === "multi_day" ? undefined : trip.duration_value,
    // Server may echo plural units ("minutes"/"hours") or "days" from a multi-day trip;
    // the duration radio only understands "minute"/"hour", so anything else stays unset.
    duration_unit: trip.duration_type === "multi_day"
      ? undefined
      : { minute: "minute", minutes: "minute", hour: "hour", hours: "hour" }[trip.duration_unit],
    cut_off_value: trip.cut_off_value,
    cut_off_unit: { minutes: "minute", hours: "hour", days: "day" }[trip.cut_off_unit] ?? trip.cut_off_unit ?? "minute",
    low_availability_enabled: trip.low_availability_enabled ?? true,
    low_availability_threshold: trip.low_availability_threshold ?? 1,

    // ── Location ─────────────────────────────────────────────────────────────
    address: trip.address,
    additional_directions: trip.additional_directions,

    // ── Ticketed Schedule ─────────────────────────────────────────────────────
    // Server slots have { id, starts_at } but no label — derive it so TicketedSchedule can display them.
    // Uses the same format as formatSlotLabel in ScheduleExperienceModal/utils.js, on the listing's clock.
    time_slots: (trip.time_slots || []).map((slot) => ({
      ...slot,
      label: slot.label || formatSlotLabelInZone(slot.starts_at, trip.time_zone)
    })),

    // ── Availability Schedule ─────────────────────────────────────────────────
    availability_schedules: (trip.trip_availability_schedules || trip.availability_schedules || []).map((schedule) => {
      const from_time = schedule.start_time ?? schedule.from_time ?? null
      const to_time = schedule.end_time ?? schedule.to_time ?? null
      const interval = schedule.increment_minutes ?? schedule.interval ?? null
      const disabled_times = timesTo12h(schedule.disabled_times ?? [])
      return {
        id: schedule.id,
        schedule_type: schedule.schedule_type,
        name: schedule.name ?? "",
        days: daysFromApi(schedule.days ?? []),
        from_time,
        to_time,
        interval,
        // Backend doesn't persist start_times — regenerate the enabled labels
        // from the rule so the schedule list still shows its time chips.
        start_times: generateStartTimes(from_time, to_time, interval, disabled_times),
        disabled_times,
      }
    }),
    // schedule_overrides is out of scope (UI commented out) — not mapped.

    // ── Multi-day schedule ────────────────────────────────────────────────────
    // For multi_day trips the wizard stores a single schedule block with all
    // start days and one start time. Seed from the first schedule entry so the
    // MultiDaySchedule component is pre-populated on edit.
    ...(() => {
      if (trip.duration_type !== "multi_day") return {}
      const schedules = trip.availability_schedules || []
      // Pick the multi-day schedule by its explicit type. Fall back to shape
      // (start_time only, no end_time / increment) for older records, then first.
      const schedule =
        schedules.find((s) => s.schedule_type === "multi_day") ||
        schedules.find((s) => !s.end_time && !s.increment_minutes) ||
        schedules[0]
      if (!schedule) return {}
      return {
        multi_day_schedule_id: schedule.id,
        multi_day_start_days: daysFromApi(schedule.days ?? []),
        multi_day_start_time: schedule.start_time ?? null,
      }
    })(),

    // ── Private Pricing ───────────────────────────────────────────────────────
    localPrices: (trip.trip_prices || []).map((p) => ({
      id: p.id,
      // A fixed multi-day booking option's length is `duration_days`; `trip_duration` is the
      // single-day length in MINUTES. The wizard keeps one `trip_duration` form field for both and
      // the payload builder writes it back to whichever column the trip's duration_type uses.
      trip_duration: p.duration_days ?? p.trip_duration,
      trip_buffer_time: p.trip_buffer_time,
      pricing_type: p.pricing_type,
      price_per_person: p.price_per_person,
      price_per_child: p.price_per_child,
      price_per_infant: p.price_per_infant,
      price_per_passenger: p.price_per_passenger,
      flat_rate: p.flat_rate,
      included_guests: p.included_guests,
      children_allowed: p.children_allowed,
      infants_allowed: p.infants_allowed,
      passengers_allowed: p.passengers_allowed,
      extra_guests_allowed: p.extra_guests_allowed,
    })),

    // ── Multi-Day Private Pricing (edit mode) ────────────────────────────────
    // Dynamic multi-day: pricing lives in flat form fields (not the localPrices array).
    // Fixed multi-day: pricing lives in localPrices (already seeded above), so skip this.
    ...(trip.duration_type === "multi_day" && trip.duration_mode !== "fixed" && trip.trip_prices?.length > 0 ? (() => {
      const p = trip.trip_prices[0]
      return {
        trip_buffer_time: p.trip_buffer_time,
        price_per_person: p.price_per_person,
        price_per_child: p.price_per_child,
        price_per_infant: p.price_per_infant,
        price_per_passenger: p.price_per_passenger,
        flat_rate: p.flat_rate,
        included_guests: p.included_guests,
        // Dedicated price-allowed keys — seeding the bare adults_allowed/children_allowed
        // here would collide with the trip-level fields (e.g. Details "child friendly",
        // seeded above from trip.children_allowed). See MULTI_DAY_PRICE_ALLOWED.
        [MULTI_DAY_PRICE_ALLOWED.ADULTS]: p.adults_allowed ?? true,
        [MULTI_DAY_PRICE_ALLOWED.CHILDREN]: p.children_allowed ?? false,
        [MULTI_DAY_PRICE_ALLOWED.INFANTS]: p.infants_allowed ?? false,
        [MULTI_DAY_PRICE_ALLOWED.PASSENGERS]: p.passengers_allowed ?? false,
      }
    })() : {}),

    // ── Additional Info ───────────────────────────────────────────────────────
    // add_on_ids — selected global add-ons for this experience (details-page selector)
    add_on_ids: trip.add_on_ids || trip.assigned_add_on_ids || (trip.add_ons || []).map((a) => a.id),
    // digital_waiver_experiences is the join table; extract the waiver IDs for the multi-select
    digital_waiver: (trip.digital_waiver_experiences || []).map((dw) => dw.digital_waiver_id),
    // Questions is single-select — backend returns the assigned id as question_template_id.
    // Fall back to the questionnaire_templates join (first entry) for older payloads.
    question_template: trip.question_template_id ?? (trip.questionnaire_templates || [])[0]?.id
  }
}
